import { createElement } from "react";
import { useQuery } from "convex/react";
import { api, BUSINESS_ID } from "./convex";
import { iRedigering, temaCss, useUtkast, type Fargeformat } from "./ascendlyForhandsvisning";

/**
 * Alt kunden kan redigere fra Ascendly-appen.
 *
 * FELT er den eneste kilden. Nettsiden leser tekstene herfra, og den samme
 * listen sendes til Convex (`sideInnhold:settKatalog`) slik at appen tegner
 * redigeringsskjermen fra den. Legger du til et felt her, dukker det opp i
 * appen uten at noen trenger ny app-versjon.
 *
 * `standard` er teksten som staar paa siden i dag. Lar eieren feltet staa
 * tomt, er det standarden som vises, saa siden ser lik ut til noen faktisk
 * skriver noe.
 */

export type FeltType = "tekst" | "flerlinje" | "bilde" | "farge" | "synlig";

export type Felt = {
  nokkel: string;
  etikett: string;
  gruppe: string;
  type: FeltType;
  hjelp?: string;
  /** Hvor paa siden teksten havner, i klartekst. Vises i appen. */
  plassering: string;
  maksLengde?: number;
  standard?: string;
  /** Fargefelt: CSS-variablene fargen settes paa. */
  cssVar?: string[];
  /** Fargefelt: «hsl» naar siden har bare tallene i variabelen («0 85% 38%»). */
  fargeformat?: Fargeformat;
};

export const FELT: Felt[] = [
  // ---------- Forsiden, toppen ----------
  {
    nokkel: "forside.hero.tittel1",
    etikett: "Overskrift, første linje",
    gruppe: "Forsiden: toppen",
    type: "tekst",
    maksLengde: 30,
    standard: "Bilvask og bilpleie",
    hjelp: "Overskriften øverst er delt i tre biter: første linje, starten av linje to, og det blå ordet sist på linje to. «Bilvask og bilpleie i Drammen» er det folk og AI-assistenter søker på, så behold de ordene.",
    plassering: "Første linje i den store overskriften øverst på forsiden, oppå bakgrunnsbildet.",
  },
  {
    nokkel: "forside.hero.uthevet",
    etikett: "Overskrift, det blå ordet",
    gruppe: "Forsiden: toppen",
    type: "tekst",
    maksLengde: 24,
    standard: "for hånd",
    plassering: "Det blå ordet sist på linje to i den store overskriften øverst på forsiden.",
  },
  {
    nokkel: "forside.hero.tittel2",
    etikett: "Overskrift, starten av linje to",
    gruppe: "Forsiden: toppen",
    type: "tekst",
    maksLengde: 30,
    standard: "i Drammen,",
    hjelp: "Står først på linje to, rett før det blå ordet.",
    plassering: "Starten av linje to i overskriften øverst på forsiden, før det blå ordet.",
  },

  // ---------- Forsiden, prisliste ----------
  {
    nokkel: "forside.priser.stikktittel",
    etikett: "Liten tekst over overskriften",
    gruppe: "Forsiden: prisliste",
    type: "tekst",
    maksLengde: 40,
    standard: "Prisliste",
    plassering: "Den lille teksten over prisoverskriften på forsiden, rett under toppbildet.",
  },
  {
    nokkel: "forside.priser.tittel",
    etikett: "Overskrift",
    gruppe: "Forsiden: prisliste",
    type: "tekst",
    maksLengde: 70,
    standard: "Priser tilpasset din bil",
    plassering: "Overskriften over de tre priskortene på forsiden.",
  },
  {
    nokkel: "forside.priser.tekst",
    etikett: "Ingress",
    gruppe: "Forsiden: prisliste",
    type: "flerlinje",
    maksLengde: 300,
    standard: "Alle våre tjenester, fra rask utvendig vask til dekkskift og oljeskift.",
    plassering: "Avsnittet under prisoverskriften på forsiden, over de tre priskortene.",
  },
  {
    nokkel: "forside.priser.ogsa",
    etikett: "Overskrift over de øvrige tjenestene",
    gruppe: "Forsiden: prisliste",
    type: "tekst",
    maksLengde: 40,
    standard: "Vi tilbyr også",
    plassering: "Overskriften i den grå boksen under priskortene, der resten av tjenestene listes opp.",
  },
  {
    nokkel: "forside.priser.lenke",
    etikett: "Tekst på lenken til prissiden",
    gruppe: "Forsiden: prisliste",
    type: "tekst",
    maksLengde: 60,
    standard: "Se alle priser og hva som er inkludert",
    plassering: "Knappen med ramme nederst i prisseksjonen på forsiden, som går videre til prissiden.",
  },

  // ---------- Forsiden, foer og etter ----------
  {
    nokkel: "forside.resultater.stikktittel",
    etikett: "Liten tekst over overskriften",
    gruppe: "Forsiden: før og etter",
    type: "tekst",
    maksLengde: 40,
    standard: "Resultater",
    plassering: "Den lille teksten over overskriften i den lyse seksjonen med før og etter-bildet.",
  },
  {
    nokkel: "forside.resultater.tittel",
    etikett: "Overskrift",
    gruppe: "Forsiden: før og etter",
    type: "tekst",
    maksLengde: 70,
    standard: "Se forskjellen selv",
    plassering: "Overskriften i den lyse seksjonen på forsiden, over bildet du kan dra i.",
  },
  {
    nokkel: "forside.resultater.tekst",
    etikett: "Ingress",
    gruppe: "Forsiden: før og etter",
    type: "flerlinje",
    maksLengde: 200,
    standard: "Dra i glidebryteren for å se før og etter.",
    plassering: "Avsnittet under overskriften i den lyse seksjonen, rett over før og etter-bildet.",
  },

  // ---------- Forsiden, det store bildet ----------
  {
    nokkel: "forside.bildebanner.tittel",
    etikett: "Overskrift oppå bildet",
    gruppe: "Forsiden: det store bildet",
    type: "tekst",
    maksLengde: 70,
    standard: "Alt bilen din trenger, på ett sted",
    hjelp: "Står i hvitt oppå et mørkt bilde, så hold den kort.",
    plassering: "Den hvite overskriften nederst til venstre på det store bildet midt på forsiden.",
  },

  // ---------- Forsiden, anmeldelser ----------
  {
    nokkel: "forside.anmeldelser.stikktittel",
    etikett: "Liten tekst over stjernene",
    gruppe: "Forsiden: anmeldelser",
    type: "tekst",
    maksLengde: 40,
    standard: "Anmeldelser",
    plassering: "Den lille teksten midtstilt over de fem stjernene i anmeldelsesseksjonen på forsiden.",
  },
  {
    nokkel: "forside.anmeldelser.merke",
    etikett: "Merket på hvert anmeldelseskort",
    gruppe: "Forsiden: anmeldelser",
    type: "tekst",
    maksLengde: 30,
    standard: "Verifisert anmeldelse",
    plassering: "Den grå teksten nede til høyre i hvert anmeldelseskort som ruller forbi.",
  },
  {
    nokkel: "forside.anmeldelser.lenke",
    etikett: "Tekst på anmeldelsesknappen",
    gruppe: "Forsiden: anmeldelser",
    type: "tekst",
    maksLengde: 60,
    standard: "Legg igjen en anmeldelse",
    plassering: "Knappen med ramme under anmeldelsene på forsiden, som åpner Google.",
  },

  // ---------- Forsiden, om oss ----------
  {
    nokkel: "forside.om.stikktittel",
    etikett: "Liten tekst over overskriften",
    gruppe: "Forsiden: om oss",
    type: "tekst",
    maksLengde: 40,
    standard: "Om oss",
    plassering: "Den lille teksten over «Om oss»-overskriften på forsiden, til venstre for videoen.",
  },
  {
    nokkel: "forside.om.tittel",
    etikett: "Overskrift",
    gruppe: "Forsiden: om oss",
    type: "tekst",
    maksLengde: 60,
    standard: "Håndverk du kan stole på",
    plassering: "Overskriften i «Om oss» på forsiden, til venstre for videoen.",
  },
  {
    nokkel: "forside.om.tekst",
    etikett: "Om bedriften",
    gruppe: "Forsiden: om oss",
    type: "flerlinje",
    maksLengde: 600,
    standard:
      "Drammen Car Wash er drevet av lidenskap for biler og detaljer. Fra vårt anlegg i Drammen leverer vi bilvask, dekkskift og oljeskift av høyeste kvalitet, for privatkunder, bedrifter og bilentusiaster som forventer mer.",
    hjelp: "Hvem dere er, hva dere er best på, og hvorfor kunden skal velge dere.",
    plassering: "Avsnittet under «Om oss»-overskriften på forsiden, til venstre for videoen.",
  },

  // ---------- Forsiden, nederst ----------
  {
    nokkel: "forside.cta.tittel",
    etikett: "Overskrift nederst",
    gruppe: "Forsiden: nederst",
    type: "tekst",
    maksLengde: 60,
    standard: "Book bilvasken din i dag.",
    hjelp: "Den siste oppfordringen før bunnteksten. Vises i veldig stor skrift.",
    plassering: "Den store midtstilte overskriften nederst på forsiden, over bookingknappen.",
  },

  // ---------- Forsiden, spoersmaal og svar ----------
  {
    nokkel: "forside.faq.stikktittel",
    etikett: "Liten tekst over overskriften",
    gruppe: "Forsiden: spørsmål og svar",
    type: "tekst",
    maksLengde: 40,
    standard: "Spørsmål & svar",
    plassering: "Den lille midtstilte teksten over spørsmålene helt nederst på forsiden.",
  },
  {
    nokkel: "forside.faq.tittel",
    etikett: "Overskrift",
    gruppe: "Forsiden: spørsmål og svar",
    type: "tekst",
    maksLengde: 70,
    standard: "Bilpleie, bilvask og dekkskift i Drammen",
    plassering: "Den midtstilte overskriften over spørsmålene helt nederst på forsiden.",
  },
  {
    nokkel: "forside.faq.lenke",
    etikett: "Tekst på lenken til alle spørsmål",
    gruppe: "Forsiden: spørsmål og svar",
    type: "tekst",
    maksLengde: 50,
    standard: "Flere spørsmål og svar →",
    plassering: "Den lille lenken under spørsmålene nederst på forsiden, som går til hele spørsmålssiden.",
  },

  // ---------- Prissiden ----------
  {
    nokkel: "priser.stikktittel",
    etikett: "Liten tekst over overskriften",
    gruppe: "Siden «Priser»",
    type: "tekst",
    maksLengde: 40,
    standard: "Prisliste",
    plassering: "Den lille teksten helt øverst på prissiden, over den store overskriften.",
  },
  {
    nokkel: "priser.tittel",
    etikett: "Overskrift",
    gruppe: "Siden «Priser»",
    type: "tekst",
    maksLengde: 40,
    standard: "Priser",
    plassering: "Den store overskriften øverst på prissiden.",
  },
  {
    nokkel: "priser.tekst",
    etikett: "Ingress",
    gruppe: "Siden «Priser»",
    type: "flerlinje",
    maksLengde: 300,
    standard: "Full oversikt over alle tjenester hos Drammen Car Wash. Prisen avhenger av bilstørrelse.",
    plassering: "Avsnittet under overskriften på prissiden, rett over pristabellen.",
  },
  {
    nokkel: "priser.tabell.tjeneste",
    etikett: "Kolonneoverskrift: tjeneste",
    gruppe: "Siden «Priser»",
    type: "tekst",
    maksLengde: 20,
    standard: "Tjeneste",
    plassering: "Overskriften på den første kolonnen i pristabellen.",
  },
  {
    nokkel: "priser.tabell.liten",
    etikett: "Kolonneoverskrift: liten bil",
    gruppe: "Siden «Priser»",
    type: "tekst",
    maksLengde: 20,
    standard: "Liten bil",
    plassering: "Overskriften på den midterste kolonnen i pristabellen.",
  },
  {
    nokkel: "priser.tabell.stor",
    etikett: "Kolonneoverskrift: stor bil",
    gruppe: "Siden «Priser»",
    type: "tekst",
    maksLengde: 20,
    standard: "Stor bil",
    plassering: "Overskriften på den siste kolonnen i pristabellen.",
  },

  // ---------- Spoersmaalssiden ----------
  {
    nokkel: "faq.stikktittel",
    etikett: "Liten tekst over overskriften",
    gruppe: "Siden «Ofte stilte spørsmål»",
    type: "tekst",
    maksLengde: 40,
    standard: "Spørsmål & svar",
    plassering: "Den lille teksten helt øverst på spørsmålssiden, over den store overskriften.",
  },
  {
    nokkel: "faq.tittel",
    etikett: "Overskrift",
    gruppe: "Siden «Ofte stilte spørsmål»",
    type: "tekst",
    maksLengde: 60,
    standard: "Ofte stilte spørsmål",
    plassering: "Den store overskriften øverst på spørsmålssiden.",
  },
  {
    nokkel: "faq.tekst",
    etikett: "Ingress",
    gruppe: "Siden «Ofte stilte spørsmål»",
    type: "flerlinje",
    maksLengde: 200,
    standard: "Finner du ikke svaret du leter etter? Ta kontakt på",
    hjelp: "Telefonnummeret og e-postadressen settes inn automatisk rett etter denne setningen, så avslutt uten punktum.",
    plassering: "Avsnittet under overskriften på spørsmålssiden, rett foran telefonnummeret og e-postadressen.",
  },

  // ---------- Gjennomgaaende tekster ----------
  {
    nokkel: "felles.bookknapp",
    etikett: "Tekst på bookingknappen",
    gruppe: "Gjennomgående tekster",
    type: "tekst",
    maksLengde: 24,
    standard: "Book time",
    hjelp: "Endrer teksten på alle bookingknappene samtidig.",
    plassering: "Alle de røde bookingknappene: øverst på forsiden, på priskortene, nederst på forsiden, på prissiden og på spørsmålssiden.",
  },
  {
    nokkel: "felles.mestpopulaer",
    etikett: "Merket på den mest populære tjenesten",
    gruppe: "Gjennomgående tekster",
    type: "tekst",
    maksLengde: 24,
    standard: "Mest populær",
    hjelp: "Hvilken tjeneste som får merket velger du på tjenesten i appen, ikke her.",
    plassering: "Det lille røde merket på tjenesten som er markert som mest populær, både på priskortet på forsiden og i pristabellen.",
  },
  // ---------- Farger (redigeringsskjermen, «Farger») ----------
  {
    nokkel: "tema.hovedfarge",
    etikett: "Hovedfarge",
    gruppe: "Farger",
    type: "farge",
    cssVar: ["--primary", "--primary-strong", "--accent", "--ring"],
    plassering: "Bookingknappene, lenkene, stjernene og de blå uthevingene på alle sider, som «for hånd» i overskriften øverst. Den blå gløden bak knappene og nederst på forsiden endres ikke.",
  },
  {
    nokkel: "tema.bakgrunn",
    etikett: "Bakgrunn",
    gruppe: "Farger",
    type: "farge",
    cssVar: ["--background", "--card", "--popover", "--surface-light"],
    plassering: "Den mørke bakgrunnen på alle sider, også bak priskortene og spørsmålene. Bildene øverst og i det store bildet på forsiden endres ikke.",
  },
  {
    nokkel: "tema.tekst",
    etikett: "Tekst",
    gruppe: "Farger",
    type: "farge",
    cssVar: ["--foreground", "--card-foreground", "--popover-foreground", "--surface-light-foreground"],
    plassering: "Overskrifter og brødtekst på alle sider. Den grå teksten under overskriftene og den hvite teksten oppå bildene endres ikke.",
  },
  // ---------- Seksjoner som kan skjules (forsiden) ----------
  {
    nokkel: "forside.priser.vis",
    etikett: "Priser",
    gruppe: "Forsiden: seksjoner",
    type: "synlig",
    standard: "ja",
    plassering: "Priskortene på forsiden, rett under toppen.",
  },
  {
    nokkel: "forside.resultater.vis",
    etikett: "Før og etter",
    gruppe: "Forsiden: seksjoner",
    type: "synlig",
    standard: "ja",
    plassering: "Før og etter-bildet av Porschen på forsiden, under priskortene.",
  },
  {
    nokkel: "forside.bildebanner.vis",
    etikett: "Det store bildet",
    gruppe: "Forsiden: seksjoner",
    type: "synlig",
    standard: "ja",
    plassering: "Det store bildet av den svarte Porschen med overskrift oppå, midt på forsiden.",
  },
  {
    nokkel: "forside.anmeldelser.vis",
    etikett: "Anmeldelser",
    gruppe: "Forsiden: seksjoner",
    type: "synlig",
    standard: "ja",
    plassering: "Stjernene og kundeanmeldelsene på forsiden.",
  },
  {
    nokkel: "forside.om.vis",
    etikett: "Om oss",
    gruppe: "Forsiden: seksjoner",
    type: "synlig",
    standard: "ja",
    plassering: "Seksjonen om dere med videoen på forsiden.",
  },
  {
    nokkel: "forside.cta.vis",
    etikett: "Book time (nederst)",
    gruppe: "Forsiden: seksjoner",
    type: "synlig",
    standard: "ja",
    plassering: "Den store overskriften med «Book time», e-post og telefon nederst på forsiden.",
  },
  {
    nokkel: "forside.faq.vis",
    etikett: "Spørsmål og svar",
    gruppe: "Forsiden: seksjoner",
    type: "synlig",
    standard: "ja",
    plassering: "Spørsmål og svar helt nederst på forsiden.",
  },
];

export type Innhold = ((nokkel: string, standard?: string) => string) & {
  /** Om en seksjon skal vises. I redigeringen alltid sant, saa den kan vises igjen. */
  vis: (nokkel: string) => boolean;
};

/** Utkast sendt inn fra appens forhaandsvisning, se «forhandsvis» i adressen. */
function utkastFraAdressen(): Map<string, string> {
  if (typeof window === "undefined") return new Map();
  try {
    const p = new URLSearchParams(window.location.search).get("forhandsvis");
    if (!p) return new Map();
    const json = JSON.parse(decodeURIComponent(escape(atob(p)))) as Record<string, string>;
    return new Map(Object.entries(json));
  } catch {
    return new Map();
  }
}

/**
 * Henter teksten for én noekkel. Rekkefoelge: utkast fra forhaandsvisningen,
 * saa det eieren har lagret, saa standarden som staar i FELT, saa det den
 * som kaller sender inn.
 */
export function useInnhold(): Innhold {
  const rader = useQuery(api.sideInnhold.offentligInnhold, { businessId: BUSINESS_ID }) as
    | { nokkel: string; verdi: string }[]
    | undefined;
  const lagret = new Map((rader ?? []).map((r) => [r.nokkel, r.verdi]));
  const utkast = useUtkast(utkastFraAdressen);
  const standarder = new Map(FELT.filter((f) => f.standard).map((f) => [f.nokkel, f.standard!]));

  const t = ((nokkel: string, standard?: string) =>
    utkast.get(nokkel) || lagret.get(nokkel) || standarder.get(nokkel) || standard || "") as Innhold;
  t.vis = (nokkel: string) => iRedigering() || t(nokkel) !== "nei";
  return t;
}

/** Merkefargene eieren har valgt i Ascendly, som CSS-variabler. Ligger én gang i rot-ruten. */
export function AscendlyTema() {
  const t = useInnhold();
  const css = temaCss(
    FELT.filter((f) => f.type === "farge" && f.cssVar).map((f): [string[], string, Fargeformat?] => [f.cssVar!, t(f.nokkel), f.fargeformat]),
  );
  return css ? createElement("style", { id: "ascendly-tema", dangerouslySetInnerHTML: { __html: css } }) : null;
}
